/**
 * @module fx/SlowMo
 * Time-scale controller for slow-motion effects.
 *
 * Three slow-mo triggers per design spec:
 *  - critical hit   — brief intense slow-mo (CONFIG.feel.slowMoScale for slowMoDurationMs)
 *  - frenzy intro   — softer slow-mo on wave start (CONFIG.feel.frenzySlowMoScale)
 *  - ease-back      — after slow-mo ends, lerp back to 1.0 over slowMoEaseMs
 *
 * prefers-reduced-motion: when the user has opted into reduced motion the
 * controller bypasses slow-mo entirely and always returns timeScale = 1.
 *
 * The game loop multiplies its frame delta by {@link SlowMo.timeScale} before
 * advancing physics, spawning, and AI — NOT before rendering (rendering runs
 * at full rate so the freeze/ease visuals are smooth).
 */

import { CONFIG } from '../game-config.js';

/** Current phase of the slow-mo state machine. */
type SlowMoPhase = 'idle' | 'active' | 'easing';

/**
 * Manages smooth slow-motion transitions.
 *
 * One instance per game session; call {@link SlowMo.reset} on restart.
 */
export class SlowMo {
  private _phase: SlowMoPhase = 'idle';
  private _targetScale = 1;
  private _elapsed = 0;
  private _duration = 0;
  private _fromScale = 1; // scale at the start of the ease-back phase
  private readonly _reducedMotion: boolean;

  /**
   * @param reducedMotion - Pass true to honour prefers-reduced-motion.
   *   In that case all slow-mo is suppressed and timeScale is always 1.
   */
  constructor(reducedMotion = false) {
    this._reducedMotion = reducedMotion;
  }

  /**
   * Triggers slow-motion for a critical fruit slash.
   * No-op when prefers-reduced-motion is active.
   */
  triggerCritical(): void {
    if (this._reducedMotion) return;
    this._startSlowMo(CONFIG.feel.slowMoScale, CONFIG.feel.slowMoDurationMs);
  }

  /**
   * Triggers slow-motion for a frenzy wave intro.
   * No-op when prefers-reduced-motion is active.
   */
  triggerFrenzy(): void {
    if (this._reducedMotion) return;
    this._startSlowMo(CONFIG.feel.frenzySlowMoScale, CONFIG.feel.frenzySlowMoDuration);
  }

  /**
   * Advances the slow-mo state machine by `deltaMs` (real time, not scaled time).
   * The game loop should pass the raw rAF delta here — not the scaled delta.
   *
   * @param deltaMs - Raw frame delta in milliseconds.
   */
  tick(deltaMs: number): void {
    if (this._phase === 'idle') return;

    this._elapsed += deltaMs;

    if (this._phase === 'active') {
      if (this._elapsed >= this._duration) {
        // Begin ease-back to 1.0.
        this._elapsed = 0;
        this._duration = CONFIG.feel.slowMoEaseMs;
        this._fromScale = this._targetScale;
        this._phase = 'easing';
      }
    } else if (this._phase === 'easing') {
      if (this._elapsed >= this._duration) {
        this._elapsed = 0;
        this._duration = 0;
        this._targetScale = 1;
        this._fromScale = 1;
        this._phase = 'idle';
      }
    }
  }

  /**
   * Current time-scale multiplier in [targetScale, 1].
   * Multiply all physics deltas by this value.
   */
  get timeScale(): number {
    if (this._reducedMotion) return 1;

    if (this._phase === 'idle') return 1;

    if (this._phase === 'active') return this._targetScale;

    if (this._phase === 'easing' && this._duration > 0) {
      // Linear ease from _fromScale → 1.
      const t = Math.min(this._elapsed / this._duration, 1);
      return this._fromScale + (1 - this._fromScale) * t;
    }

    return 1;
  }

  /** Whether slow-motion or easing is currently active. */
  get active(): boolean {
    return this._phase !== 'idle';
  }

  /** Resets all state (e.g. on game restart). */
  reset(): void {
    this._phase = 'idle';
    this._targetScale = 1;
    this._elapsed = 0;
    this._duration = 0;
    this._fromScale = 1;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _startSlowMo(scale: number, durationMs: number): void {
    this._targetScale = scale;
    this._duration = durationMs;
    this._elapsed = 0;
    this._fromScale = scale;
    this._phase = 'active';
  }
}
